import { Loader2 } from 'lucide-react';
import { useUsage } from '../../hooks/useUsage';

const nf = new Intl.NumberFormat('it-IT');

// TAB Consumo AI: riepilogo call/token + tabelle by_model e by_day (solo Tailwind, nessun grafico)
export function UsageTab() {
  const { data, isLoading, isError } = useUsage();

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 size={16} className="animate-spin text-zinc-600" />
      </div>
    );
  }
  if (isError || !data) {
    return <p className="py-8 text-center text-xs text-zinc-500">Dati di consumo non disponibili.</p>;
  }

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-sm font-semibold text-zinc-100">Consumo AI</h3>
        <p className="text-[11px] text-zinc-500">
          Tenant: <span className="font-mono text-zinc-400">{data.tenant}</span>
        </p>
      </div>

      {/* Riepilogo totali */}
      <div className="grid grid-cols-3 gap-3">
        <StatCard label="Call totali" value={nf.format(data.total_calls)} />
        <StatCard label="Token input" value={nf.format(data.total_input_tokens)} />
        <StatCard label="Token output" value={nf.format(data.total_output_tokens)} />
      </div>

      {/* Per modello */}
      <div>
        <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
          Per modello
        </h4>
        <UsageTable
          rows={data.by_model.map((m) => ({
            label: m.model,
            calls: m.calls,
            input: m.input_tokens,
            output: m.output_tokens,
          }))}
          firstCol="Modello"
        />
      </div>

      {/* Per giorno */}
      {data.by_day?.length > 0 && (
        <div>
          <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
            Per giorno
          </h4>
          <UsageTable
            rows={data.by_day.map((d) => ({
              label: d.day,
              calls: d.calls,
              input: d.input_tokens,
              output: d.output_tokens,
            }))}
            firstCol="Giorno"
          />
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-zinc-800 bg-zinc-950/40 p-3">
      <div className="text-[10px] font-medium uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="mt-1 text-lg font-semibold text-zinc-100">{value}</div>
    </div>
  );
}

function UsageTable({
  rows,
  firstCol,
}: {
  rows: { label: string; calls: number; input: number; output: number }[];
  firstCol: string;
}) {
  if (rows.length === 0) {
    return <p className="text-xs text-zinc-500">Nessun dato.</p>;
  }
  return (
    <div className="overflow-hidden rounded-md border border-zinc-800">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-zinc-800 bg-zinc-950/40 text-left text-[10px] uppercase tracking-wider text-zinc-500">
            <th className="px-3 py-1.5 font-medium">{firstCol}</th>
            <th className="px-3 py-1.5 text-right font-medium">Call</th>
            <th className="px-3 py-1.5 text-right font-medium">Token in</th>
            <th className="px-3 py-1.5 text-right font-medium">Token out</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-800/70">
          {rows.map((r) => (
            <tr key={r.label} className="text-zinc-300">
              <td className="px-3 py-1.5 font-mono text-[11px] text-zinc-400">{r.label}</td>
              <td className="px-3 py-1.5 text-right">{nf.format(r.calls)}</td>
              <td className="px-3 py-1.5 text-right">{nf.format(r.input)}</td>
              <td className="px-3 py-1.5 text-right">{nf.format(r.output)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
